import { QueryClient, type DefaultOptions } from '@tanstack/react-query'
import { getErrorMessage } from './axios'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// React Query Configuration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const queryConfig: DefaultOptions = {
  queries: {
    // Refetch configuration
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,

    // Retry configuration
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error && typeof error === 'object' && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status
        if (status && status >= 400 && status < 500) {
          return false
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Stale time configuration
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)

    // Error handling
    throwOnError: false,
  },
  mutations: {
    // Retry configuration for mutations
    retry: false,

    // Error handling
    throwOnError: false,

    // Global mutation callbacks
    onError: (error) => {
      const message = getErrorMessage(error)
      console.error('[Mutation Error]', message)
    },
  },
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Query Client Instance
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
})

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Query Keys Factory
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const queryKeys = {
  news: {
    all: ['news'] as const,
    search: (keywords: string[]) => ['news', 'search', { keywords }] as const,
    byKeyword: (keyword: string, params?: Record<string, unknown>) =>
      ['news', 'keyword', keyword, params] as const,
  },
} as const

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Query Client Helpers
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Invalidate all news queries
 */
export function invalidateNewsQueries() {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.news.all,
  })
}

/**
 * Remove all news queries from cache
 */
export function removeNewsQueries() {
  return queryClient.removeQueries({
    queryKey: queryKeys.news.all,
  })
}

/**
 * Prefetch news search
 */
export function prefetchNewsSearch(keywords: string[]) {
  // This would require the actual query function
  // Implementation depends on the API layer
  console.log('Prefetching news search for:', keywords)
}
